import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Ban, Copy, Pencil, Trash2, Users } from 'lucide-react';
import { hrListJobs, hrDeleteJob, hrCloseJob } from '../../lib/jobsApi';
import type { HrJob } from '../../lib/jobsApi';
import { formatRelativeDate } from '../../lib/formatRelativeDate';
import { hrErrorMessage } from '../../lib/tenancyErrorCopy';

/*
 * Velaigal /hr/jobs — "My jobs" list.
 *
 * One row per job the signed-in recruiter owns, actions scoped tightly to
 * status: a draft can only be edited or deleted (nothing to show a
 * candidate yet), a published job can be shared/closed/reviewed, and a
 * closed job is read-only except for reviewing who applied. The title
 * links through to the /hr/jobs/:id management view (JobManage.tsx) for
 * the fuller read-only picture (tasks + rubric); this list stays a
 * scannable table of rows, not a second copy of that detail page.
 *
 * hrListJobs already joins applicationCount server-side (see hrJobs.ts),
 * so there's nothing to reconcile here beyond removing/patching a row
 * in place after a delete or close.
 */

const STATUS_LABEL: Record<HrJob['status'], string> = {
  draft: 'Draft',
  published: 'Published',
  closed: 'Closed',
};

// Saffron is reserved for the "live" state (Published); draft/closed get the
// neutral hairline override — same rule ApplicationStatusChip follows in
// src/pages/JobSeeker/Applications.tsx.
const StatusChip: React.FC<{ status: HrJob['status'] }> = ({ status }) => {
  const isSaffron = status === 'published';
  return (
    <span
      className="chip"
      style={isSaffron ? undefined : { backgroundColor: 'var(--color-hairline)', color: 'var(--color-ink-secondary)' }}
    >
      <span
        aria-hidden="true"
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: isSaffron ? 'var(--color-saffron-press)' : 'var(--color-ink-tertiary)' }}
      />
      {STATUS_LABEL[status]}
    </span>
  );
};

function applicantLabel(count: number | undefined): string {
  const n = count ?? 0;
  return `${n} applicant${n === 1 ? '' : 's'}`;
}

function copyPublicLink(jobId: string): void {
  const link = `${window.location.origin}/jobs/${jobId}`;
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(link).then(
      () => toast.success('Public link copied.'),
      () => toast.error('Could not copy the link.'),
    );
  } else {
    toast.error('Could not copy — copy it manually from the address shown.');
  }
}

const InlineSpinner: React.FC<{ label: string }> = ({ label }) => (
  <>
    <span
      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
      style={{ borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }}
      aria-hidden="true"
    />
    <span>{label}</span>
  </>
);

const LoadingBlock: React.FC = () => (
  <div role="status" className="py-16 text-center">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-6 text-sm text-ink-tertiary">Loading your jobs.</p>
  </div>
);

const ErrorBlock: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="py-16 text-center">
    <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h2 className="font-display text-2xl font-medium text-ink leading-snug mt-4">Couldn&apos;t load your jobs.</h2>
    <p className="mt-4 text-sm text-ink-secondary max-w-md mx-auto leading-relaxed">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary mt-8">
      Try again
    </button>
  </div>
);

const EmptyBlock: React.FC = () => (
  <div className="bg-paper border border-hairline rounded-md p-8 text-center">
    <p className="text-ink mb-6">You haven&apos;t posted a job yet.</p>
    <Link to="/hr/create-job" className="btn-primary">
      Create your first job
    </Link>
  </div>
);

interface JobRowProps {
  job: HrJob;
  onDeleted: (jobId: string) => void;
  onClosed: (jobId: string) => void;
}

const JobRow: React.FC<JobRowProps> = ({ job, onDeleted, onClosed }) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await hrDeleteJob(job.id);
      onDeleted(job.id);
    } catch (err) {
      toast.error(hrErrorMessage(err, 'Could not delete this job.'));
      setBusy(false);
      setConfirmingDelete(false);
    }
  };

  const handleClose = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await hrCloseJob(job.id);
      onClosed(job.id);
    } catch (err) {
      toast.error(hrErrorMessage(err, 'Could not close this job.'));
      setBusy(false);
      setConfirmingClose(false);
    }
  };

  return (
    <article className="card">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap mb-2">
            <StatusChip status={job.status} />
            <span className="text-xs text-ink-tertiary">{applicantLabel(job.applicationCount)}</span>
          </div>
          <Link
            to={`/hr/jobs/${job.id}`}
            className="font-display text-lg font-medium text-ink velaigal-headline-hover inline-block"
          >
            {job.title}
          </Link>
          <p className="mt-1 text-xs text-ink-tertiary">
            {job.publishedAt ? `Published ${formatRelativeDate(job.publishedAt)}` : 'Not yet published'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap flex-shrink-0">
          {job.status === 'draft' && (
            <>
              <Link
                to={`/hr/jobs/${job.id}/edit`}
                className="btn-secondary"
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
              >
                <Pencil className="h-3 w-3" strokeWidth={1.75} />
                Edit
              </Link>
              {confirmingDelete ? (
                <>
                  <button
                    type="button"
                    className="btn-danger"
                    style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    onClick={() => void handleDelete()}
                    disabled={busy}
                  >
                    {busy ? <InlineSpinner label="Deleting…" /> : 'Confirm delete'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    onClick={() => setConfirmingDelete(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="text-ink-tertiary hover:text-error transition-colors"
                  aria-label={`Delete ${job.title}`}
                  onClick={() => setConfirmingDelete(true)}
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.75} />
                </button>
              )}
            </>
          )}

          {job.status === 'published' && (
            <>
              <Link
                to={`/hr/jobs/${job.id}/applicants`}
                className="btn-secondary"
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
              >
                <Users className="h-3 w-3" strokeWidth={1.75} />
                View applicants
              </Link>
              <button
                type="button"
                className="btn-secondary"
                style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                onClick={() => copyPublicLink(job.id)}
              >
                <Copy className="h-3 w-3" strokeWidth={1.75} />
                Copy public link
              </button>
              {confirmingClose ? (
                <>
                  <button
                    type="button"
                    className="btn-danger"
                    style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    onClick={() => void handleClose()}
                    disabled={busy}
                  >
                    {busy ? <InlineSpinner label="Closing…" /> : 'Confirm close'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                    onClick={() => setConfirmingClose(false)}
                    disabled={busy}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
                  onClick={() => setConfirmingClose(true)}
                >
                  <Ban className="h-3 w-3" strokeWidth={1.75} />
                  Close
                </button>
              )}
            </>
          )}

          {job.status === 'closed' && (
            <Link
              to={`/hr/jobs/${job.id}/applicants`}
              className="btn-secondary"
              style={{ padding: '0.375rem 0.75rem', fontSize: '0.75rem' }}
            >
              <Users className="h-3 w-3" strokeWidth={1.75} />
              View applicants
            </Link>
          )}
        </div>
      </div>

      {confirmingClose && (
        <p className="mt-3 pt-3 border-t border-hairline text-xs text-ink-tertiary">
          Closing stops new applications — candidates keep their submissions.
        </p>
      )}
    </article>
  );
};

const MyJobs: React.FC = () => {
  const [jobs, setJobs] = useState<HrJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);

  // Cancelled-guarded so a fast unmount (or a stale in-flight retry) can't
  // setState afterward — mirrors the load-by-id effect in CreateJob.tsx.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await hrListJobs();
        if (cancelled) return;
        setJobs(res.jobs);
      } catch (err) {
        if (cancelled) return;
        setError(hrErrorMessage(err, 'Check your connection and try again.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadNonce]);

  const handleDeleted = (jobId: string) => {
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
    toast.success('Job deleted.');
  };

  const handleClosed = (jobId: string) => {
    setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, status: 'closed' } : j)));
    toast.success('Job closed.');
  };

  return (
    <div className="max-w-3xl mx-auto">
      <span className="label-editorial block mb-3">My jobs</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">Your jobs</h1>

      {loading ? (
        <LoadingBlock />
      ) : error ? (
        <ErrorBlock message={error} onRetry={() => setLoadNonce((n) => n + 1)} />
      ) : jobs.length === 0 ? (
        <EmptyBlock />
      ) : (
        <div className="space-y-4">
          {jobs.map((job) => (
            <JobRow key={job.id} job={job} onDeleted={handleDeleted} onClosed={handleClosed} />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyJobs;
